import { useState } from 'react';
import { Send, AlertCircle, CheckCircle2, Users, MapPin, UserCheck } from 'lucide-react';
import {
  pushAPI,
  type PushNotificationType,
  type PushSendPayload,
  type PushSendResult,
} from '../../services/api';

type TargetMode = 'userIds' | 'filter' | 'todos';

const TYPES: { value: PushNotificationType; label: string }[] = [
  { value: 'fatura', label: 'Fatura' },
  { value: 'suporte', label: 'Suporte' },
  { value: 'promocao', label: 'Promoção' },
  { value: 'manutencao', label: 'Manutenção' },
  { value: 'sistema', label: 'Sistema' },
  { value: 'custom', label: 'Custom' },
];

export default function EnviarTab() {
  const [targetMode, setTargetMode] = useState<TargetMode>('userIds');
  const [userIdsText, setUserIdsText] = useState('');
  const [cidade, setCidade] = useState('');
  const [bairro, setBairro] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [type, setType] = useState<PushNotificationType>('sistema');
  const [priority, setPriority] = useState<'normal' | 'high'>('normal');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PushSendResult | null>(null);

  function parseUserIds(): string[] {
    return userIdsText
      .split(/[;\n\r,\s]+/)
      .map((s) => s.trim())
      .filter(Boolean);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setResult(null);

    if (!title.trim() || !body.trim()) {
      setError('Título e mensagem são obrigatórios.');
      return;
    }

    const payload: PushSendPayload = {
      payload: { title: title.trim(), body: body.trim(), priority },
      type,
    };

    if (targetMode === 'userIds') {
      const ids = parseUserIds();
      if (ids.length === 0) {
        setError('Forneça ao menos 1 CPF/CNPJ ou userId.');
        return;
      }
      payload.userIds = ids;
    } else if (targetMode === 'filter') {
      if (!cidade.trim() && !bairro.trim()) {
        setError('Informe cidade e/ou bairro para o filtro.');
        return;
      }
      payload.filter = {};
      if (cidade.trim()) payload.filter.cidade = cidade.trim();
      if (bairro.trim()) payload.filter.bairro = bairro.trim();
    } else {
      // Broadcast em massa: todos os devices ativos
      payload.broadcast = true;
    }

    setLoading(true);
    try {
      const r = await pushAPI.send(payload);
      setResult(r);
      // Limpar form se tudo OK
      if (r.result.sent > 0) {
        setTitle('');
        setBody('');
      }
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      setError(e.response?.data?.message ?? e.message ?? 'Erro ao enviar.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="bg-surface border border-line rounded-2xl p-6">
      <h2 className="text-lg font-semibold text-fg mb-1 flex items-center gap-2">
        <Send size={18} className="text-primary" />
        Enviar notificação
      </h2>
      <p className="text-sm text-fg-2 mb-6">
        Dispara push notifications para o app Central do Cliente.
      </p>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Target mode */}
        <div>
          <label className="block text-sm font-medium text-fg-2 mb-2">Destinatários</label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <TargetCard
              active={targetMode === 'userIds'}
              icon={<UserCheck size={18} />}
              label="Por CPF/CNPJ"
              desc="Lista específica"
              onClick={() => setTargetMode('userIds')}
            />
            <TargetCard
              active={targetMode === 'filter'}
              icon={<MapPin size={18} />}
              label="Por região"
              desc="Cidade / bairro"
              onClick={() => setTargetMode('filter')}
            />
            <TargetCard
              active={targetMode === 'todos'}
              icon={<Users size={18} />}
              label="Todos"
              desc="Broadcast em massa"
              onClick={() => setTargetMode('todos')}
            />
          </div>
        </div>

        {targetMode === 'todos' && (
          <div className="flex items-start gap-2 rounded-lg border border-warning-line/50 bg-warning-soft/20 px-4 py-3 text-sm text-warning">
            <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
            <span>
              A notificação será enviada pra <strong>todos os dispositivos ativos</strong>.
              Confira o título e a mensagem antes de disparar.
            </span>
          </div>
        )}

        {targetMode === 'userIds' && (
          <div>
            <label className="block text-sm font-medium text-fg-2 mb-2">
              CPFs/CNPJs (separados por vírgula, ponto-e-vírgula ou linha)
            </label>
            <textarea
              value={userIdsText}
              onChange={(e) => setUserIdsText(e.target.value)}
              rows={4}
              placeholder="123.456.789-00&#10;987.654.321-00&#10;..."
              className="w-full px-3 py-2 bg-canvas border border-line rounded-lg text-fg text-sm placeholder-slate-600 focus:border-primary focus:outline-none"
            />
            <p className="text-xs text-muted mt-1">
              {parseUserIds().length} destinatário(s) detectado(s)
            </p>
          </div>
        )}

        {targetMode === 'filter' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field
              label="Cidade"
              value={cidade}
              onChange={setCidade}
              placeholder="ex: São Paulo"
            />
            <Field
              label="Bairro (opcional)"
              value={bairro}
              onChange={setBairro}
              placeholder="ex: Centro"
            />
          </div>
        )}

        {/* Conteúdo */}
        <div className="border-t border-line pt-5 space-y-4">
          <Field
            label="Título"
            value={title}
            onChange={setTitle}
            placeholder="Ex: Fatura disponível"
            required
          />
          <div>
            <label className="block text-sm font-medium text-fg-2 mb-1">Mensagem</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={3}
              required
              placeholder="Conteúdo da notificação..."
              className="w-full px-3 py-2 bg-canvas border border-line rounded-lg text-fg text-sm placeholder-slate-600 focus:border-primary focus:outline-none"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-fg-2 mb-1">Tipo</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value as PushNotificationType)}
                className="w-full px-3 py-2 bg-canvas border border-line rounded-lg text-fg text-sm focus:border-primary focus:outline-none"
              >
                {TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-fg-2 mb-1">Prioridade</label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value as 'normal' | 'high')}
                className="w-full px-3 py-2 bg-canvas border border-line rounded-lg text-fg text-sm focus:border-primary focus:outline-none"
              >
                <option value="normal">Normal</option>
                <option value="high">Alta</option>
              </select>
            </div>
          </div>
        </div>

        {error && (
          <div className="flex items-start gap-2 p-3 bg-danger-soft/20 border border-danger-line/50 rounded-lg text-sm text-danger">
            <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {result && (
          <div className="p-4 bg-canvas border border-line rounded-lg">
            <div className="flex items-center gap-2 mb-3">
              {result.result.sent > 0 ? (
                <CheckCircle2 size={18} className="text-success" />
              ) : (
                <AlertCircle size={18} className="text-warning" />
              )}
              <h3 className="font-medium text-fg">Resultado do envio</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <Stat label="Usuários alvo" value={result.targetUserCount} />
              <Stat label="Dispositivos" value={result.devicesCount} />
              <Stat label="Entregues" value={result.result.sent} good />
              <Stat label="Falhas" value={result.result.failed} bad={result.result.failed > 0} />
            </div>
            {result.result.invalidTokens.length > 0 && (
              <p className="mt-3 text-xs text-muted">
                {result.result.invalidTokens.length} token(s) inválido(s) foram desativados
                automaticamente.
              </p>
            )}
          </div>
        )}

        <div className="flex justify-end pt-2">
          <button
            type="submit"
            disabled={loading}
            className="flex items-center gap-2 px-5 py-2.5 bg-primary-solid hover:bg-primary-solid-hover disabled:bg-active disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition"
          >
            <Send size={16} />
            {loading ? 'Enviando…' : 'Enviar notificação'}
          </button>
        </div>
      </form>
    </div>
  );
}

function TargetCard({
  active,
  icon,
  label,
  desc,
  onClick,
  disabled,
}: {
  active: boolean;
  icon: React.ReactNode;
  label: string;
  desc: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`flex items-start gap-3 p-3 rounded-lg border text-left transition ${
        active
          ? 'border-primary bg-primary-soft/20'
          : disabled
          ? 'border-line bg-canvas/50 opacity-50 cursor-not-allowed'
          : 'border-line bg-canvas hover:border-active'
      }`}
    >
      <div
        className={`w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 ${
          active ? 'bg-primary-solid text-white' : 'bg-hover text-fg-2'
        }`}
      >
        {icon}
      </div>
      <div>
        <div className="text-sm font-medium text-fg">{label}</div>
        <div className="text-xs text-muted">{desc}</div>
      </div>
    </button>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  required,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  required?: boolean;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-fg-2 mb-1">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className="w-full px-3 py-2 bg-canvas border border-line rounded-lg text-fg text-sm placeholder-slate-600 focus:border-primary focus:outline-none"
      />
    </div>
  );
}

function Stat({
  label,
  value,
  good,
  bad,
}: {
  label: string;
  value: number;
  good?: boolean;
  bad?: boolean;
}) {
  const color = good ? 'text-success' : bad ? 'text-danger' : 'text-fg';
  return (
    <div>
      <div className="text-xs text-muted uppercase tracking-wide">{label}</div>
      <div className={`text-xl font-bold ${color}`}>{value}</div>
    </div>
  );
}
